// Funnel: signup → onboarded → first try-on → first purchase → repeat purchase.
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";

export default function AdminFunnel() {
  const [data, setData] = useState<{
    signups: Set<string>;
    onboarded: Set<string>;
    triedOn: Set<string>;
    paid: Set<string>;
    repeatPaid: Set<string>;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const since = new Date(Date.now() - 30 * 86400_000).toISOString();
      const [pRes, tRes, ctRes] = await Promise.all([
        supabase.from("profiles").select("user_id,onboarded,created_at").gte("created_at", since).limit(10000),
        supabase.from("tryons").select("user_id,created_at").gte("created_at", since).limit(10000),
        supabase
          .from("credit_transactions")
          .select("user_id,reason,created_at")
          .eq("reason", "purchase")
          .gte("created_at", since)
          .limit(10000),
      ]);
      const profiles = pRes.data ?? [];
      const tryons = tRes.data ?? [];
      const purchases = ctRes.data ?? [];

      const signups = new Set(profiles.map((p) => p.user_id));
      const onboarded = new Set(profiles.filter((p) => p.onboarded).map((p) => p.user_id));
      const triedOn = new Set<string>();
      for (const t of tryons) if (signups.has(t.user_id)) triedOn.add(t.user_id);
      const paid = new Set<string>();
      const purchaseCounts = new Map<string, number>();
      for (const c of purchases) {
        if (!signups.has(c.user_id)) continue;
        paid.add(c.user_id);
        purchaseCounts.set(c.user_id, (purchaseCounts.get(c.user_id) ?? 0) + 1);
      }
      const repeatPaid = new Set<string>();
      for (const [u, n] of purchaseCounts.entries()) if (n >= 2) repeatPaid.add(u);

      if (!active) return;
      setData({ signups, onboarded, triedOn, paid, repeatPaid });
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, []);

  const steps = useMemo(() => {
    if (!data) return [];
    const base = data.signups.size;
    const mk = (label: string, n: number) => ({
      label,
      n,
      pct: base ? (n / base) * 100 : 0,
    });
    return [
      mk("Signed up", data.signups.size),
      mk("Onboarded", data.onboarded.size),
      mk("First try-on", data.triedOn.size),
      mk("First purchase", data.paid.size),
      mk("Repeat purchase", data.repeatPaid.size),
    ];
  }, [data]);

  if (loading || !data) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">Funnel</h1>
        <p className="text-sm text-muted-foreground">
          Cohort = users who signed up in the last 30 days.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Conversion steps</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {steps.map((s, i) => {
            const prev = i > 0 ? steps[i - 1] : null;
            const dropPct = prev && prev.n > 0 ? ((prev.n - s.n) / prev.n) * 100 : 0;
            return (
              <div key={s.label} className="rounded-lg border p-3">
                <div className="flex items-baseline justify-between">
                  <div className="font-medium">{s.label}</div>
                  <div className="text-sm tabular-nums">
                    <span className="font-semibold">{s.n.toLocaleString()}</span>{" "}
                    <span className="text-muted-foreground">({s.pct.toFixed(1)}%)</span>
                  </div>
                </div>
                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full bg-primary transition-all"
                    style={{ width: `${s.pct}%` }}
                  />
                </div>
                {prev && (
                  <div className="mt-1 text-[10px] text-muted-foreground">
                    Drop-off from previous: {dropPct.toFixed(1)}%
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
